/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { Pagination as ReactPagination, PaginationItem, PaginationLink } from "reactstrap";
import "./style/Pagination.css";

const PER_PAGE = 24;

type PaginationProps = {
	page: number;
	onPageChange: (nextPage: number) => any;
	total?: number;
	perPage?: number;
};

function Pagination({ page, onPageChange, total, perPage = PER_PAGE }: PaginationProps) {
	const numberOfPages = Math.ceil((total || 0) / perPage);
	const [visiblePages, setVisiblePages] = useState<(number | string)[]>([]);

	useEffect(() => {
		setVisiblePages(getVisiblePages(page, numberOfPages));
	}, [total, page, perPage, numberOfPages]);

	if (numberOfPages <= 1) return null;

	const handleClick = (p: number | string, e: React.MouseEvent) => {
		e.preventDefault();
		if (typeof p === "number") {
			onPageChange(p);
		}
	};

	const renderItem = (p: number | string, i: number) => {
		const isActive = p === page;
		const isEllipsis = p === "...";

		return (
			<PaginationItem
				key={`${p}-${i}`}
				active={isActive}
				disabled={isEllipsis}
				className={`pagination-page-item ${isActive ? "active" : ""} ${isEllipsis ? "ellipsis" : ""}`}
			>
				<PaginationLink
					href="#"
					onClick={(e) => handleClick(p, e)}
				>
					{p}
				</PaginationLink>
			</PaginationItem>
		);
	};

	return (
		<nav className="pagination-custom-wrapper" aria-label="Pagination">
			<ReactPagination className="pagination-custom" size="sm">
				<PaginationItem disabled={page <= 1}>
					<PaginationLink
						previous
						href="#"
						onClick={(e) => {
							e.preventDefault();
							if (page > 1) onPageChange(page - 1);
						}}
					>
						‹
					</PaginationLink>
				</PaginationItem>

				{visiblePages.map((p, i) => renderItem(p, i))}

				<PaginationItem disabled={page >= numberOfPages}>
					<PaginationLink
						next
						href="#"
						onClick={(e) => {
							e.preventDefault();
							if (page < numberOfPages) onPageChange(page + 1);
						}}
					>
						›
					</PaginationLink>
				</PaginationItem>
			</ReactPagination>
		</nav>
	);
}

function getVisiblePages(current: number, total: number): (number | string)[] {
	if (total <= 7) {
		return Array.from({ length: total }, (_, i) => i + 1);
	}

	const pages: (number | string)[] = [];
	const leftSibling = Math.max(current - 1, 2);
	const rightSibling = Math.min(current + 1, total - 1);

	const showLeftEllipsis = leftSibling > 2;
	const showRightEllipsis = rightSibling < total - 1;

	pages.push(1);

	if (showLeftEllipsis) {
		pages.push("...");
	}

	for (let i = leftSibling; i <= rightSibling; i++) {
		pages.push(i);
	}

	if (showRightEllipsis) {
		pages.push("...");
	}

	pages.push(total);

	return pages;
}

export default Pagination;
